import {usePlantConfig} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox, CardLinkWrapper} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {MajorOutageLeadEvents} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useMechanicalElectricalCockpitEvents} from '../hooks'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
  isMobile: boolean
  onEventClick?: () => void
  shiftSummaryLink?: string
}

export const MajorOutageLeadEventsContainer = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx,
  isMobile,
  shiftSummaryLink,
  onEventClick
}: Props) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const {
    data: plantConfig,
    isLoading: isPlantConfigLoading,
    isError: isPlantConfigError,
    isRefetching: isPlantConfigRefetching,
    refetch: refetchPlantConfig
  } = usePlantConfig({plantId, language})
  const shifts = plantConfig?.shifts || []
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    isError: isEventsError,
    isRefetching: isEventsRefetching,
    refetch: refetchEvents
  } = useMechanicalElectricalCockpitEvents({
    plantId,
    timezone,
    shifts
  })

  const hasShifts = !!shifts.length

  return (
    <CardLinkWrapper href={shiftSummaryLink} data-test-id="events-card-link-to-shifthandover">
      <CardBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...cardBoxSx,
          px: 0,
          pl: 3
        }}
        data-test-id="events-card"
      >
        <Box display="flex">
          <Box flex={1}>
            <Typography variant="h3" mb={3}>
              {t('majorOutageLeadEvents.cardTitle')}
            </Typography>
          </Box>
          {hasShifts && (
            <Chip
              icon={<CalendarTodayOutlined />}
              label={t('majorOutageLeadEvents.label.pastShift')}
              size="small"
              sx={{mx: 1, mr: 3}}
            />
          )}
        </Box>
        <DataContentWrapper
          showLoader={isEventsLoading || isPlantConfigLoading}
          isError={isEventsError || isPlantConfigError}
          isRefetching={isEventsRefetching || isPlantConfigRefetching}
          data={eventsData?.events}
          noDataSettings={{
            ...(!hasShifts && {
              noDataMessage: t('message.shiftsNotEntered')
            })
          }}
          retryFunction={() => {
            if (isPlantConfigError) void refetchPlantConfig()
            if (isEventsError) void refetchEvents()
          }}
          progressSx={{color: 'primary.main'}}
          placeholderWrapperSx={placeholderWrapperSx}
        >
          <Box sx={{overflowY: 'auto', pr: 3}}>
            {eventsData?.events && (
              <MajorOutageLeadEvents
                events={eventsData.events}
                isMobile={isMobile}
                onEventClick={onEventClick}
              />
            )}
          </Box>
        </DataContentWrapper>
      </CardBox>
    </CardLinkWrapper>
  )
}
